import { z } from 'zod';

import { buildQueryString } from '../../utils/querystring';
import { summaryGroupingOptions } from '../analytics/analytics';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';
import { IMediaReferenceSchema } from '../media/media';

export enum Rating {
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
}

export enum Recommendability {
  ONE = 1,
  TWO = 2,
  THREE = 3,
  FOUR = 4,
  FIVE = 5,
  SIX = 6,
  SEVEN = 7,
  EIGHT = 8,
  NINE = 9,
  TEN = 10,
}

export const PostReviewRequestSchema = z.object({
  rating: z.coerce.number().transform((n) => {
    const res = z.nativeEnum(Rating).safeParse(n);
    // If the value is invalid, take the best rating
    return res.success ? res.data : Rating.FIVE;
  }),
  recommendability: z.coerce.number().transform((n) => {
    const res = z.nativeEnum(Recommendability).safeParse(n);
    // If the value is invalid, take the best recommendability
    return res.success ? res.data : Recommendability.TEN;
  }),
  text: z.string(),
  token: z.string(),
});

export const PostReviewEndpoint = makeEndpoint({
  method: 'POST',
  url: () => 'v1/reviews',
  responseSchema: z.void(),
  requestSchema: PostReviewRequestSchema,
});

export const BrandsReviewsSummaryResponseSchema = z
  .object({
    brandId: z.string(),
    host: z
      .object({
        id: z.string(),
        fullName: z.string(),
        profilePicture: IMediaReferenceSchema.nullable(),
      })
      .optional(),
    brand: z
      .object({
        id: z.string(),
        name: z.string(),
        logo: IMediaReferenceSchema.nullable(),
      })
      .optional(),
    experience: z
      .object({
        id: z.string(),
        title: z.string(),
        coverPicture: IMediaReferenceSchema.nullable(),
      })
      .optional(),
    resourceGroup: z
      .object({
        id: z.string(),
        title: z.string(),
        resourceGroupCollectionId: z.string(),
        coverPicture: IMediaReferenceSchema.nullable(),
      })
      .optional(),
    totalReviews: z.string(),
    averageRating: z.string(),
    npsScore: z.string().nullable(),
    reviewPercentage: z.string(),
  })
  .strict();

const GetBrandsReviewsSummaryResponseSchema = paginateResponseSchema(BrandsReviewsSummaryResponseSchema);

const GetBrandsReviewsSummaryQuerySchema = paginateQuerySchema({
  sortBy: ['averageRating', 'npsScore', 'totalReviews', 'reviewPercentage'],
})
  .omit({ filters: true })
  .extend({
    sortOrder: z.enum(['ASC', 'DESC']).optional(),
    groupBy: z.enum(summaryGroupingOptions),
  });

export const GetBrandsReviewsSummaryEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    entityType,
    entityId,
    query,
  }: {
    entityType: string;
    entityId: string;
    query: z.input<typeof GetBrandsReviewsSummaryQuerySchema>;
  }) => `v1/${entityType}/${entityId}/review-summary?${buildQueryString(query)}`,
  responseSchema: GetBrandsReviewsSummaryResponseSchema,
  requestSchema: z.void(),
  querySchema: GetBrandsReviewsSummaryQuerySchema,
});
